import { CompanyInfos } from './company-infos.model';
import { UserGroupTypeEnum } from './enums/userGroupType.enum.model';

export class UserGroup {
  id: string;
  name: string;
  companyInfos: CompanyInfos;
  isProducer: boolean;
  supplierId: string;
  /** A group may hold several roles at once, e.g. ['PRODUCER', 'AGENCY']. */
  types: UserGroupTypeEnum[];
  createdAt: Date;
  createdBy: string;
  lastUpdateAt: Date;
  /**
   * Users attached to this group, computed on the fly by the user service
   * (PUL-3022). Only filled on AGENCY groups, hence optional.
   */
  activeUsersCount?: number;
  inactiveUsersCount?: number;
  canBook?: boolean;
  bookingDeniedMessage?: string | null;
  /**
   * Registration details the agency still lacks (PUL-3224), computed by the user
   * service on the group listings. Empty once complete, absent on a non-agency.
   */
  missingFields?: string[] | null;
  /** Last time the completion link was emailed to the agency. */
  completionRequestedAt?: string | null;
}

/**
 * Whether an agency lacks registration details (PUL-3224). Only flagged, never
 * blocking: the backoffice may still activate it. Unknown (no `missingFields`,
 * older user service) counts as complete, so nothing is flagged by mistake.
 */
export function isAgencyIncomplete(group?: UserGroup): boolean {
  return (group?.missingFields?.length ?? 0) > 0;
}

/**
 * Single source of truth for what an agency is on the front side. `types` is
 * authoritative: it is filled by the user service and backs the
 * Supplier.agencyCount computation.
 *
 * Do not re-derive this from companyInfos (agencyCode, reference...): those
 * fields are often empty and used to give diverging results across screens.
 */
export function isAgencyGroup(group?: UserGroup): boolean {
  return !!group?.types?.includes(UserGroupTypeEnum.AGENCY);
}

/** Supplier (distributor) group, same `types` rule as {@link isAgencyGroup}. */
export function isSupplierGroup(group?: UserGroup): boolean {
  return !!group?.types?.includes(UserGroupTypeEnum.SUPPLIER);
}

/**
 * Whether at least one user — activated or not — is attached to this group
 * (PUL-3022). An agency with no user can never yield a result, so the admin
 * screens hide it.
 *
 * Missing counters mean the user service does not send them (yet): the group is
 * kept, so an out-of-sync backend degrades to the previous behaviour instead of
 * emptying the whole list.
 */
export function hasUsers(group?: UserGroup): boolean {
  const active = group?.activeUsersCount;
  const inactive = group?.inactiveUsersCount;
  if (active == null && inactive == null) {
    return true;
  }
  return (active ?? 0) + (inactive ?? 0) > 0;
}

/**
 * Sort key for {@link UserGroup.createdAt} (PUL-3223). `compare` treats any
 * falsy key as "no value" and pins it at the same end whatever the direction,
 * so a missing or invalid date maps to -Infinity to always sort as the oldest.
 */
export function createdAtSortValue(group?: UserGroup): number {
  const time = group?.createdAt ? new Date(group.createdAt).getTime() : NaN;
  return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time;
}
